import React, { useState, useEffect } from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import Loader from "../../../../components/Common/Loader";
import Slider from "react-slick";
import { APPINTRO_DETAIL } from "../../../../services/ApiCalls";
import { useTranslation } from "react-i18next";

// status -> badge color mapping (matches the list page)
const STATUS_BADGE_CLASS = {
  active: "bg-success",
  inactive: "bg-secondary",
  archived: "bg-dark",
};

const AppintroDetail = () => {
  const [introDetail, setIntroDetail] = useState();
  const [loader, setLoader] = useState(false);
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getData = async () => {
    try {
      setLoader(true);
      const res = await APPINTRO_DETAIL({ id });
      if (res?.data?.status === "success") {
        setIntroDetail(res.data?.data);
      }
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (id) getData();
  }, [id]);

  // media now comes back as [{ fileId: { _id, link }, order, _id }, ...]
  // sort by the manually-set order before rendering.
  const sortedMedia = [...(introDetail?.media || [])].sort(
    (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
  );

  return (
    <>
      {loader && <Loader />}
      <section className="roomDetail py-3 position-relative">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-2">
                <Link
                  to={`/appintro?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {t("appIntro.detailTitle")}
                </h4>
              </div>
            </Col>

            <Col lg="12" className="my-2">
              <Card className="p-3 shadow-sm rounded">
                <h5 className="fw-bold mb-3">
                  {t("appIntro.images", "Images")}
                </h5>

                {sortedMedia.length > 0 ? (
                  sortedMedia.length === 1 ? (
                    <div className="p-2">
                      <img
                        src={sortedMedia[0]?.fileId?.link}
                        alt="app-intro-0"
                        className="rounded shadow-sm"
                        style={{
                          width: "100%",
                          height: 250,
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  ) : (
                    <Slider
                      dots={true}
                      infinite={sortedMedia.length > 1}
                      speed={500}
                      slidesToShow={Math.min(sortedMedia.length, 2)}
                      slidesToScroll={1}
                      arrows={sortedMedia.length > 1}
                      responsive={[
                        {
                          breakpoint: 1024,
                          settings: {
                            slidesToShow: Math.min(sortedMedia.length, 3),
                          },
                        },
                        {
                          breakpoint: 768,
                          settings: {
                            slidesToShow: Math.min(sortedMedia.length, 2),
                          },
                        },
                        { breakpoint: 480, settings: { slidesToShow: 1 } },
                      ]}
                    >
                      {sortedMedia.map((item, i) => (
                        <div key={item?._id ?? i} className="p-2">
                          <img
                            src={item?.fileId?.link}
                            alt={`app-intro-${i}`}
                            className="rounded shadow-sm"
                            style={{
                              width: "100%",
                              height: 200,
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      ))}
                    </Slider>
                  )
                ) : (
                  <p className="text-muted">
                    {t("common.noImages", "No Images")}
                  </p>
                )}
              </Card>
            </Col>

            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                {introDetail ? (
                  <Row className="justify-content-between">
                    <Col md={6} className="my-2">
                      <ul className="list-unstyled ps-0 mb-0 notLastBorder pe-lg-3">
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("appIntro.columns.description")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {introDetail?.description}
                          </h6>
                        </li>

                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("appIntro.columns.type", "Type")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50 text-capitalize">
                            {introDetail?.type}
                          </h6>
                        </li>

                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("appIntro.columns.lang", "Language")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {introDetail?.lang}
                          </h6>
                        </li>

                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.createdAt")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {moment(introDetail?.createdAt).format(
                              "YYYY-MM-DD"
                            )}
                          </h6>
                        </li>
                      </ul>
                    </Col>

                    <Col md={6} className="my-2">
                      <ul className="list-unstyled mb-0 notLastBorder ps-lg-3">
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("appIntro.columns.title", "Title")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {introDetail?.title}
                          </h6>
                        </li>

                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.status", "Status")}:
                          </p>
                          <h6 className="m-0 w-50">
                            <span
                              className={`badge text-capitalize ${
                                STATUS_BADGE_CLASS[introDetail?.status] ||
                                "bg-secondary"
                              }`}
                            >
                              {introDetail?.status}
                            </span>
                          </h6>
                        </li>

                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("appIntro.columns.order", "Order")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {introDetail?.order}
                          </h6>
                        </li>

                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t(
                              "appIntro.columns.backgroundColor",
                              "Background Color"
                            )}
                            :
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50 d-flex align-items-center gap-2">
                            <span
                              style={{
                                display: "inline-block",
                                width: 18,
                                height: 18,
                                borderRadius: 4,
                                backgroundColor: introDetail?.backgroundColor,
                                border: "1px solid #ccc",
                              }}
                            />
                            {introDetail?.backgroundColor}
                          </h6>
                        </li>

                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.updatedAt")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {moment(introDetail?.updatedAt).format(
                              "YYYY-MM-DD"
                            )}
                          </h6>
                        </li>
                      </ul>
                    </Col>
                  </Row>
                ) : (
                  <p className="text-center text-muted">
                    No app intro details found
                  </p>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AppintroDetail;